import { memo, useEffect, useRef, useState } from 'react';
import { CircularProgress } from '@mui/material';
import { NumericFormat } from 'react-number-format';
import { useQuery } from 'react-query';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import useAuth from 'hooks/useAuth';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useTopPanel from 'hooks/useTopPanel';
import ObjectBlockMultiFilter from 'components/ui/filters/items/ObjectBlockMultiFilter';
import HomeResidentTypeFilter from 'components/ui/filters/items/HomeResidentTypeFilter';
import DateRangeFilter from 'components/ui/filters/items/DateRangeFilter';
import BarChart from 'components/ui/charts/BarChart';
import moment from 'moment';
import ContractsModal from './ContractsModal';

// -----------------------------------------------------------------------------
// Helper cards
// -----------------------------------------------------------------------------

const Money = ({ value, suffix }) => (
  <NumericFormat
    value={value}
    displayType='text'
    thousandSeparator=' '
    decimalScale={0}
    allowNegative={false}
    suffix={suffix}
    className='font-bold'
  />
);

const StatusCard = ({ name, count, color, icon }) => (
  <div
    className='rounded-xl bg-white p-4 shadow-sm border-l-4 flex items-center gap-3'
    style={{ borderLeftColor: color }}
  >
    <div
      className='w-10 h-10 rounded-lg flex items-center justify-center text-lg'
      style={{ background: `${color}1a`, color: color }}
    >
      <i className={icon} />
    </div>
    <div className='flex-1 min-w-0'>
      <div className='text-xs text-gray-500 truncate'>{name}</div>
      <div className='text-xl font-bold text-gray-800'>{count}</div>
    </div>
  </div>
);

/**
 * Mijozlar haqdorligi (qarzdorlik) - ikkiga bo'lingan karta.
 * Muddati o'tgan (qizil) + Muddati kelmagan (sariq).
 */
const DebtRow = ({ color, label, icon, sum, valute }) => (
  <div
    className='flex flex-col gap-0.5 p-2 rounded-lg'
    style={{ background: `${color}10` }}
  >
    <div
      className='flex items-center gap-1.5 text-xs font-medium'
      style={{ color }}
    >
      <i className={icon} />
      {label}
    </div>
    <div className='flex flex-col leading-tight pl-5'>
      {sum > 0 ? (
        <div className='text-sm text-gray-800'>
          <Money value={sum} suffix=' UZS' />
        </div>
      ) : null}
      {valute > 0 ? (
        <div className='text-sm text-gray-800'>
          <Money value={valute} suffix=' $' />
        </div>
      ) : null}
      {!sum && !valute ? (
        <div className='text-sm text-gray-400'>0</div>
      ) : null}
    </div>
  </div>
);

const DebtCard = ({
  title,
  overdueLabel,
  upcomingLabel,
  overdue,
  upcoming,
}) => (
  <div className='rounded-xl bg-white p-4 shadow-sm border base-border flex flex-col gap-2'>
    <div className='flex items-center gap-2'>
      <div
        className='w-9 h-9 rounded-lg flex items-center justify-center text-base'
        style={{ background: '#f59e0b1a', color: '#f59e0b' }}
      >
        <i className='bi bi-exclamation-circle' />
      </div>
      <div className='text-sm font-medium text-gray-600 flex-1'>{title}</div>
    </div>
    <div className='flex flex-col gap-2 mt-1'>
      <DebtRow
        color='#ef4444'
        icon='bi bi-clock-history'
        label={overdueLabel}
        sum={overdue?.sum || 0}
        valute={overdue?.valute || 0}
      />
      <DebtRow
        color='#f59e0b'
        icon='bi bi-hourglass-split'
        label={upcomingLabel}
        sum={upcoming?.sum || 0}
        valute={upcoming?.valute || 0}
      />
    </div>
  </div>
);

const KpiCard = ({ title, icon, color, sum, valute, footer }) => (
  <div className='rounded-xl bg-white p-4 shadow-sm border base-border flex flex-col gap-2'>
    <div className='flex items-center gap-2'>
      <div
        className='w-9 h-9 rounded-lg flex items-center justify-center text-base'
        style={{ background: `${color}1a`, color: color }}
      >
        <i className={icon} />
      </div>
      <div className='text-sm font-medium text-gray-600 flex-1'>{title}</div>
    </div>
    <div className='flex flex-col gap-1 mt-1'>
      {sum > 0 ? (
        <div className='text-lg text-gray-800'>
          <Money value={sum} suffix=' UZS' />
        </div>
      ) : null}
      {valute > 0 ? (
        <div className='text-lg text-gray-800'>
          <Money value={valute} suffix=' $' />
        </div>
      ) : null}
      {!sum && !valute ? (
        <div className='text-lg text-gray-400'>0</div>
      ) : null}
    </div>
    {footer ? (
      <div className='text-xs text-gray-500 pt-1 border-t mt-1'>{footer}</div>
    ) : null}
  </div>
);

// Home::list ids: 1=ACTIVE(Bo'sh), 2=TIME(Band), 3=ORDERED(Sotilgan), 4=DEACTIVE(Aktiv emas)
const STATUS_META = {
  1: { color: '#3b82f6', icon: 'bi bi-house' },         // Bo'sh – blue
  2: { color: '#f59e0b', icon: 'bi bi-house-lock' },    // Band  – amber
  3: { color: '#22c55e', icon: 'bi bi-house-check' },   // Sotilgan – green
  4: { color: '#ef4444', icon: 'bi bi-house-x' },       // Aktiv emas – red
};

// -----------------------------------------------------------------------------
// Page
// -----------------------------------------------------------------------------

const Dashboard = () => {
  const { setComponent } = useTopPanel();
  const { t, i18n } = useTranslation();
  const [{ user }] = useAuth();
  const axiosPrivate = useAxiosPrivate();
  const { search } = useLocation();

  useEffect(() => {
    setComponent(
      <div className='component-title'>{t('boss.dashboard.title')}</div>
    );
  }, [i18n.language]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['boss-dashboard', search],
    queryFn: async () => {
      const response = await axiosPrivate.get(
        `/analytic/boss-dashboard/index${search || ''}`
      );
      return response.data.data;
    },
    retry: false,
  });

  const loading = isLoading || isFetching;

  // ── Kutilayotgan tushumlar (BI to'lov analitikasidagi `expected`) ──
  // Obyekt filtri mos kelishi uchun objectId → object_id ga o'giriladi.
  const expectedParams = (() => {
    const sp = new URLSearchParams(search || '');
    const objectId = sp.get('objectId');
    return objectId ? `?object_id=${objectId}` : '';
  })();

  const { data: expectedData } = useQuery({
    queryKey: ['boss-dashboard-expected', expectedParams],
    queryFn: async () => {
      const response = await axiosPrivate.get(
        `/boss/bi/payments${expectedParams}`
      );
      return response.data.data;
    },
    retry: false,
  });

  const currentYm = moment().format('YYYY-MM');
  const expectedAll = expectedData?.expected || [];
  // Uch aniq raqam: muddati o'tgan (ym=null) / joriy oy / kelasi 12 oy jami
  const overdue = expectedAll.find((e) => !e.ym) || { sum: 0, valute: 0 };
  const currentMonth = expectedAll.find((e) => e.ym === currentYm) || {
    ym: currentYm,
    sum: 0,
    valute: 0,
  };
  const upcoming = expectedAll
    .filter((e) => e.ym && e.ym > currentYm)
    .slice(0, 12);
  const upcomingTotal = upcoming.reduce(
    (acc, e) => ({
      sum: acc.sum + (Number(e.sum) || 0),
      valute: acc.valute + (Number(e.valute) || 0),
    }),
    { sum: 0, valute: 0 }
  );
  // Diagramma oralig'i — URL'dagi exp_from / exp_till (DateRangeFilter), default:
  // joriy oy → kelasi 12 oy. Oraliq joriy oydan boshlansa (yoki undan oldin) chapda
  // "Muddati o'tgan" (joriy oydan oldingi to'lanmaganlar) ustuni ham ko'rsatiladi.
  const expSp = new URLSearchParams(search || '');
  const rangeFromYm = expSp.get('exp_from')
    ? moment(expSp.get('exp_from')).format('YYYY-MM')
    : currentYm;
  const rangeTillYm = expSp.get('exp_till')
    ? moment(expSp.get('exp_till')).format('YYYY-MM')
    : moment().add(12, 'months').format('YYYY-MM');
  const rangeMonths = [];
  for (
    let m = moment(rangeFromYm, 'YYYY-MM');
    m.isSameOrBefore(moment(rangeTillYm, 'YYYY-MM'), 'month') && rangeMonths.length < 60;
    m.add(1, 'month')
  ) {
    const ym = m.format('YYYY-MM');
    rangeMonths.push(
      expectedAll.find((e) => e.ym === ym) || { ym, sum: 0, valute: 0 }
    );
  }
  const showOverdueBar = rangeFromYm <= currentYm;
  const expectedMonths = showOverdueBar
    ? [{ ...overdue, ym: null, overdue: true }, ...rangeMonths]
    : rangeMonths;
  const expectedLabels = expectedMonths.map((e) =>
    e.overdue ? "Muddati o'tgan" : moment(e.ym, 'YYYY-MM').format('MM.YYYY')
  );
  const expectedHasValute = expectedAll.some((e) => Number(e.valute) > 0);
  const expectedHasSum = expectedAll.some((e) => Number(e.sum) > 0);
  // Valyuta tanlagich: ikkalasi bo'lsa foydalanuvchi almashtiradi
  const [expectedCur, setExpectedCur] = useState('sum');
  const activeCur =
    expectedCur === 'valute' && expectedHasValute
      ? 'valute'
      : expectedHasSum
        ? 'sum'
        : 'valute';

  // ── Shartnomalar yopilishi (mijoz to'lovlari tugash oylari) ──
  const finishAll = data?.payment_finish || [];
  const nowYm = moment();
  // Barcha oylar — diagramma yonga scroll qilinadi (oyga ~64px), joriy oyga suriladi
  const finishWindow = [...finishAll].sort((a, b) =>
    String(a.ym).localeCompare(String(b.ym))
  );
  const finishLabels = finishWindow.map((r) =>
    moment(r.ym, 'YYYY-MM').format('MM.YYYY')
  );
  const BAR_PX = 64;
  const finishScrollRef = useRef(null);
  useEffect(() => {
    const el = finishScrollRef.current;
    if (!el || !finishWindow.length) return;
    const idx = finishWindow.findIndex((r) => r.ym >= currentYm);
    if (idx > 0) {
      el.scrollLeft = Math.max(0, idx * BAR_PX - el.clientWidth / 2);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finishAll.length]);
  const next3Ym = nowYm.clone().add(3, 'months').format('YYYY-MM');
  const finishThisMonth =
    Number(finishAll.find((r) => r.ym === currentYm)?.finishing) || 0;
  const finishNext3 = finishAll
    .filter((r) => r.ym > currentYm && r.ym <= next3Ym)
    .reduce((a, r) => a + (Number(r.finishing) || 0), 0);
  const finishedThisYear = finishAll
    .filter((r) => String(r.ym).startsWith(nowYm.format('YYYY')))
    .reduce((a, r) => a + (Number(r.finished) || 0), 0);

  // ── Diagramma/karta bosilganda — tegishli shartnomalar modali ──
  const [contractsOpen, setContractsOpen] = useState(false);
  const [contractsList, setContractsList] = useState([]);
  const [contractsLoading, setContractsLoading] = useState(false);
  const [contractsParams, setContractsParams] = useState('');
  const [excelLoading, setExcelLoading] = useState(false);

  // Excel: server faylni tayyorlab bir martalik havola beradi — shu havoladan yuklaymiz
  const downloadExcel = async (path, qs) => {
    if (excelLoading) return;
    setExcelLoading(true);
    try {
      const response = await axiosPrivate.get(
        `/analytic/boss-dashboard/${path}${qs ? `?${qs}` : ''}`
      );
      const link = response?.data?.data?.link;
      if (link) {
        window.location.href = `${process.env.REACT_APP_BACKEND_URL}${link}`;
      }
    } finally {
      setExcelLoading(false);
    }
  };

  const openContracts = async (params) => {
    if (contractsLoading) return;
    setContractsLoading(true);
    try {
      // Dashboard'dagi obyekt/blok filtrlari ham uzatiladi
      const sp = new URLSearchParams(search || '');
      Object.entries(params).forEach(([k, v]) => {
        if (v !== null && v !== undefined && v !== '') sp.set(k, v);
      });
      const response = await axiosPrivate.get(
        `/analytic/boss-dashboard/contracts?${sp.toString()}`
      );
      setContractsList(response?.data?.data || []);
      setContractsParams(sp.toString());
      setContractsOpen(true);
    } finally {
      setContractsLoading(false);
    }
  };

  const prevYm = nowYm.clone().subtract(1, 'months').format('YYYY-MM');
  const nextYm = nowYm.clone().add(1, 'months').format('YYYY-MM');
  const next12Ym = nowYm.clone().add(12, 'months').format('YYYY-MM');

  const statuses = data?.home_statuses?.statuses || [];
  const total = data?.home_statuses?.total || 0;

  return (
    <div className='component-wrapper boss-dashboard-wrapper'>
      {/* Header */}
      <div className='dashboard-actions flex items-center justify-between gap-4 mb-4'>
        <div className='dashboard-owner-greeting'>
          {t('boss.dashboard.welcome')},{' '}
          <span className='text-xl font-medium'>{user?.user?.name}</span>
        </div>
        <div className='flex items-center gap-3 flex-wrap justify-end'>
          <div className='w-[220px] shrink-0'>
            <HomeResidentTypeFilter />
          </div>
          <div className='w-[280px] shrink-0'>
            <ObjectBlockMultiFilter />
          </div>
        </div>
      </div>

      {loading ? (
        <div className='flex justify-center py-12'>
          <CircularProgress size={36} />
        </div>
      ) : (
        <div className='flex flex-col gap-6'>
          {/* Jami maydon, Jami sotilgan maydon va Qolgan maydon — eng yuqorida */}
          <div className='grid grid-cols-1 sm:grid-cols-3 gap-3'>
            {/* Jami maydon */}
            <div className='rounded-xl bg-white p-4 shadow-sm border base-border flex items-center gap-3'>
              <div
                className='w-11 h-11 rounded-lg flex items-center justify-center text-xl'
                style={{ background: '#3b82f61a', color: '#3b82f6' }}
              >
                <i className='bi bi-aspect-ratio' />
              </div>
              <div>
                <div className='text-sm font-medium text-gray-600'>
                  {t('boss.dashboardV2.totalSquare')}
                </div>
                <div className='text-2xl font-semibold text-gray-800'>
                  <NumericFormat
                    value={data?.total_square || 0}
                    displayType='text'
                    thousandSeparator=' '
                    decimalScale={2}
                  />{' '}
                  m²
                </div>
                {/* Terassa alohida — yashash maydoniga qo'shilmagan */}
                <div className='text-xs text-gray-500 mt-0.5'>
                  <i className='bi bi-crop mr-1' />
                  Terassa:{' '}
                  <NumericFormat
                    value={data?.total_terassa || 0}
                    displayType='text'
                    thousandSeparator=' '
                    decimalScale={2}
                  />{' '}
                  m²
                </div>
              </div>
            </div>

            {/* Jami sotilgan maydon */}
            <div className='rounded-xl bg-white p-4 shadow-sm border base-border flex items-center gap-3'>
              <div
                className='w-11 h-11 rounded-lg flex items-center justify-center text-xl'
                style={{ background: '#10b9811a', color: '#10b981' }}
              >
                <i className='bi bi-rulers' />
              </div>
              <div>
                <div className='text-sm font-medium text-gray-600'>
                  {t('boss.dashboardV2.soldSquare')}
                </div>
                <div className='text-2xl font-semibold text-gray-800'>
                  <NumericFormat
                    value={data?.sold_square || 0}
                    displayType='text'
                    thousandSeparator=' '
                    decimalScale={2}
                  />{' '}
                  m²
                </div>
                <div className='text-xs text-gray-500 mt-0.5'>
                  <i className='bi bi-crop mr-1' />
                  Terassa:{' '}
                  <NumericFormat
                    value={data?.sold_terassa || 0}
                    displayType='text'
                    thousandSeparator=' '
                    decimalScale={2}
                  />{' '}
                  m²
                </div>
              </div>
            </div>

            {/* Qolgan maydon — Jami maydon - Jami sotilgan maydon (shu sahifadagi
                ikkala kartaga hamisha mos kelsin uchun frontendda hisoblanadi) */}
            <div className='rounded-xl bg-white p-4 shadow-sm border base-border flex items-center gap-3'>
              <div
                className='w-11 h-11 rounded-lg flex items-center justify-center text-xl'
                style={{ background: '#f59e0b1a', color: '#f59e0b' }}
              >
                <i className='bi bi-bounding-box-circles' />
              </div>
              <div>
                <div className='text-sm font-medium text-gray-600'>
                  {t('boss.dashboardV2.remainingSquare')}
                </div>
                <div className='text-2xl font-semibold text-gray-800'>
                  <NumericFormat
                    value={(data?.total_square || 0) - (data?.sold_square || 0)}
                    displayType='text'
                    thousandSeparator=' '
                    decimalScale={2}
                  />{' '}
                  m²
                </div>
                <div className='text-xs text-gray-500 mt-0.5'>
                  <i className='bi bi-crop mr-1' />
                  Terassa:{' '}
                  <NumericFormat
                    value={
                      (data?.total_terassa || 0) - (data?.sold_terassa || 0)
                    }
                    displayType='text'
                    thousandSeparator=' '
                    decimalScale={2}
                  />{' '}
                  m²
                </div>
              </div>
            </div>
          </div>

          {/* 1. Uy statuslari */}
          <section>
            <div className='text-base font-semibold mb-3 flex items-center gap-2'>
              <i className='bi bi-building text-blue-500' />
              {t('boss.dashboardV2.homeStatuses')}
            </div>
            <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3'>
              {statuses.map((s) => (
                <StatusCard
                  key={s.id}
                  name={s.name}
                  count={s.count}
                  color={STATUS_META[s.id]?.color || '#6b7280'}
                  icon={STATUS_META[s.id]?.icon || 'bi bi-house'}
                />
              ))}
              <StatusCard
                name={t('boss.dashboardV2.total')}
                count={total}
                color='#6366f1'
                icon='bi bi-grid-3x3-gap-fill'
              />
            </div>
          </section>

          {/* 2-5. Asosiy KPI'lar */}
          <section>
            <div className='text-base font-semibold mb-3 flex items-center gap-2'>
              <i className='bi bi-graph-up text-green-500' />
              {t('boss.dashboardV2.financialKpi')}
            </div>
            <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4'>
              {/* 2. Undirilgan mablag' */}
              <KpiCard
                title={t('boss.dashboardV2.collected')}
                icon='bi bi-cash-coin'
                color='#22c55e'
                sum={data?.collected?.sum || 0}
                valute={data?.collected?.valute || 0}
              />

              {/* 3. Mijozlardagi haqdorlik - muddati o'tgan va kelmagan */}
              <DebtCard
                title={t('boss.dashboardV2.outstanding')}
                overdueLabel={t('boss.dashboardV2.overdue')}
                upcomingLabel={t('boss.dashboardV2.upcoming')}
                overdue={data?.outstanding?.overdue}
                upcoming={data?.outstanding?.upcoming}
              />

              {/* 4. O'rtacha kv/m² narxi */}
              <KpiCard
                title={t('boss.dashboardV2.avgPrice')}
                icon='bi bi-bar-chart-line'
                color='#3b82f6'
                sum={data?.avg_price_m2?.sum || 0}
                valute={data?.avg_price_m2?.valute || 0}
                footer={t('boss.dashboardV2.avgPriceHint')}
              />

              {/* 5. Qolgan uylar potensiali */}
              <KpiCard
                title={t('boss.dashboardV2.remainingPotential')}
                icon='bi bi-graph-up-arrow'
                color='#a855f7'
                sum={data?.remaining_potential?.sum || 0}
                valute={data?.remaining_potential?.valute || 0}
                footer={
                  <>
                    {data?.remaining_potential?.homes_count || 0}{' '}
                    {t('boss.dashboardV2.remainingHomes')} ·{' '}
                    <NumericFormat
                      value={data?.remaining_potential?.square || 0}
                      displayType='text'
                      thousandSeparator=' '
                      decimalScale={2}
                    />{' '}
                    m²
                    {Number(data?.remaining_potential?.terassa) > 0 && (
                      <>
                        {' '}· terassa{' '}
                        <NumericFormat
                          value={data?.remaining_potential?.terassa || 0}
                          displayType='text'
                          thousandSeparator=' '
                          decimalScale={2}
                        />{' '}
                        m²
                      </>
                    )}
                  </>
                }
              />
            </div>
          </section>

          {/* 6. Kutilayotgan tushumlar — 3 ta aniq raqam + oylar diagrammasi */}
          <section>
            <div className='text-base font-semibold mb-3 flex items-center gap-2'>
              <i className='bi bi-calendar-check text-indigo-500' />
              Kutilayotgan tushumlar
              <button
                type='button'
                onClick={() =>
                  downloadExcel('summary-excel', (search || '').replace(/^\?/, ''))
                }
                disabled={excelLoading}
                className='ml-auto text-xs font-medium px-2.5 py-1 rounded-lg border base-border bg-white hover:bg-gray-50 text-green-700 flex items-center gap-1.5'
              >
                <i className='bi bi-file-earmark-excel' />
                Excel
              </button>
            </div>
            <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4'>
              <div
                className='cursor-pointer'
                onClick={() => openContracts({ mode: 'expected', till_ym: prevYm })}
              >
                <KpiCard
                  title="Muddati o'tgan"
                  icon='bi bi-exclamation-triangle'
                  color='#dc2626'
                  sum={overdue.sum || 0}
                  valute={overdue.valute || 0}
                  footer="Shu kungacha to'lanmagan to'lovlar · bosing → shartnomalar"
                />
              </div>
              <div
                className='cursor-pointer'
                onClick={() =>
                  openContracts({ mode: 'expected', from_ym: currentYm, till_ym: currentYm })
                }
              >
                <KpiCard
                  title='Joriy oy'
                  icon='bi bi-calendar-event'
                  color='#16a34a'
                  sum={currentMonth.sum || 0}
                  valute={currentMonth.valute || 0}
                  footer={`${moment().format('MM.YYYY')} oyida kutilmoqda · bosing → shartnomalar`}
                />
              </div>
              <div
                className='cursor-pointer'
                onClick={() =>
                  openContracts({ mode: 'expected', from_ym: nextYm, till_ym: next12Ym })
                }
              >
                <KpiCard
                  title='Kelasi 12 oy'
                  icon='bi bi-calendar-range'
                  color='#0057FF'
                  sum={upcomingTotal.sum}
                  valute={upcomingTotal.valute}
                  footer="Keyingi oylar to'lov jadvali bo'yicha · bosing → shartnomalar"
                />
              </div>
            </div>

            {(expectedHasSum || expectedHasValute) && (
              <div className='rounded-xl bg-white p-4 shadow-sm border base-border'>
                <div className='flex items-center justify-between gap-3 mb-3 flex-wrap'>
                  <div className='text-sm font-medium text-gray-600'>
                    Oylar bo'yicha — muddati o'tgan (qizil), joriy oy (yashil),
                    tanlangan oraliq (ko'k)
                  </div>
                  <div className='flex items-center gap-3 flex-wrap'>
                    <DateRangeFilter
                      size='small'
                      queries={['exp_from', 'exp_till']}
                      labels={{
                        start: t('common.filter.dateFrom'),
                        end: t('common.table.till'),
                      }}
                    />
                  {expectedHasSum && expectedHasValute && (
                    <div className='flex rounded-lg border base-border overflow-hidden text-sm'>
                      <button
                        type='button'
                        onClick={() => setExpectedCur('sum')}
                        className={`px-3 py-1 ${
                          activeCur === 'sum'
                            ? 'bg-base-color text-white'
                            : 'bg-white text-gray-600'
                        }`}
                      >
                        So'm
                      </button>
                      <button
                        type='button'
                        onClick={() => setExpectedCur('valute')}
                        className={`px-3 py-1 ${
                          activeCur === 'valute'
                            ? 'bg-base-color text-white'
                            : 'bg-white text-gray-600'
                        }`}
                      >
                        $
                      </button>
                    </div>
                  )}
                  </div>
                </div>
                {/* Ko'p oy tanlansa — yonga scroll */}
                <div className='overflow-x-auto my-scroll'>
                <div
                  className='h-[300px]'
                  style={{ width: `max(100%, ${expectedMonths.length * BAR_PX}px)` }}
                >
                  <BarChart
                    chartLabels={expectedLabels}
                    chartDatasets={[
                      {
                        label:
                          activeCur === 'sum'
                            ? "Kutilmoqda (so'm)"
                            : 'Kutilmoqda ($)',
                        data: expectedMonths.map(
                          (e) => Number(e[activeCur]) || 0
                        ),
                        backgroundColor: expectedMonths.map((e) =>
                          e.overdue
                            ? '#dc2626'
                            : e.ym === currentYm
                              ? '#16a34a'
                              : '#0057FF'
                        ),
                        borderRadius: 6,
                      },
                    ]}
                    optionPlugins={{ legend: { display: false } }}
                    options={{ scales: { y: { beginAtZero: true } } }}
                    onClick={(els) => {
                      const e = els?.length && expectedMonths[els[0].index];
                      if (!e) return;
                      if (e.overdue) {
                        // Joriy oydan oldingi to'lanmaganlar
                        openContracts({ mode: 'expected', till_ym: prevYm });
                      } else if (e.ym) {
                        openContracts({ mode: 'expected', from_ym: e.ym, till_ym: e.ym });
                      }
                    }}
                  />
                </div>
                </div>
              </div>
            )}
          </section>

          {/* 7. Shartnomalar yopilishi — raqamlar + oylar diagrammasi */}
          {finishAll.length > 0 && (
            <section>
              <div className='text-base font-semibold mb-3 flex items-center gap-2'>
                <i className='bi bi-flag-fill text-emerald-500' />
                Shartnomalar yopilishi
              <button
                  type='button'
                  onClick={() =>
                    downloadExcel('summary-excel', (search || '').replace(/^\?/, ''))
                  }
                  disabled={excelLoading}
                  className='ml-auto text-xs font-medium px-2.5 py-1 rounded-lg border base-border bg-white hover:bg-gray-50 text-green-700 flex items-center gap-1.5'
                >
                  <i className='bi bi-file-earmark-excel' />
                  Excel
                </button>
              </div>
              <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4'>
                <div
                  className='cursor-pointer'
                  onClick={() =>
                    openContracts({ mode: 'finish', type: 'finishing', from_ym: currentYm, till_ym: currentYm })
                  }
                >
                  <StatusCard
                    name="Bu oy to'lovi tugaydi"
                    count={`${finishThisMonth} ta`}
                    color='#16a34a'
                    icon='bi bi-calendar-check'
                  />
                </div>
                <div
                  className='cursor-pointer'
                  onClick={() =>
                    openContracts({ mode: 'finish', type: 'finishing', from_ym: nextYm, till_ym: next3Ym })
                  }
                >
                  <StatusCard
                    name='Kelasi 3 oyda tugaydi'
                    count={`${finishNext3} ta`}
                    color='#0057FF'
                    icon='bi bi-calendar-week'
                  />
                </div>
                <div
                  className='cursor-pointer'
                  onClick={() =>
                    openContracts({
                      mode: 'finish',
                      type: 'finished',
                      from_ym: `${nowYm.format('YYYY')}-01`,
                      till_ym: `${nowYm.format('YYYY')}-12`,
                    })
                  }
                >
                  <StatusCard
                    name="Bu yil to'liq to'langan"
                    count={`${finishedThisYear} ta`}
                    color='#6366f1'
                    icon='bi bi-check2-circle'
                  />
                </div>
              </div>
              {finishWindow.length > 0 && (
                <div className='rounded-xl bg-white p-4 shadow-sm border base-border'>
                  <div className='text-sm font-medium text-gray-600 mb-3'>
                    Qaysi oyda nechta shartnoma yopiladi — yashil: to'liq
                    to'langan, ko'k: jadval bo'yicha oxirgi to'lovi shu oyda
                  </div>
                  <div ref={finishScrollRef} className='overflow-x-auto my-scroll'>
                  <div
                    className='h-[300px]'
                    style={{ width: `max(100%, ${finishWindow.length * BAR_PX}px)` }}
                  >
                    <BarChart
                      chartLabels={finishLabels}
                      chartDatasets={[
                        {
                          label: "To'liq to'langan",
                          data: finishWindow.map((r) => Number(r.finished) || 0),
                          backgroundColor: '#16a34a',
                          borderRadius: 6,
                        },
                        {
                          label: 'Tugaydi (jadval)',
                          data: finishWindow.map((r) => Number(r.finishing) || 0),
                          backgroundColor: '#0057FF',
                          borderRadius: 6,
                        },
                      ]}
                      optionPlugins={{
                        legend: {
                          display: true,
                          position: 'bottom',
                          labels: { usePointStyle: true },
                        },
                      }}
                      options={{
                        scales: {
                          y: { beginAtZero: true, ticks: { precision: 0 } },
                        },
                      }}
                      onClick={(els) => {
                        if (!els?.length) return;
                        const r = finishWindow[els[0].index];
                        if (!r?.ym) return;
                        openContracts({
                          mode: 'finish',
                          type: els[0].datasetIndex === 0 ? 'finished' : 'finishing',
                          from_ym: r.ym,
                          till_ym: r.ym,
                        });
                      }}
                    />
                  </div>
                  </div>
                </div>
              )}
            </section>
          )}
        </div>
      )}

      {/* Diagramma/karta bosilganda — shartnomalar ro'yxati */}
      {contractsOpen && (
        <ContractsModal
          open={contractsOpen}
          setOpen={setContractsOpen}
          data={contractsList}
          excelLoading={excelLoading}
          onExcel={() => downloadExcel('contracts-excel', contractsParams)}
        />
      )}
    </div>
  );
};

export default memo(Dashboard);
